/**
 * Balloon Service
 * 
 * Unified service for balloon operations:
 * - Fetching existing balloons by slug or ID
 * - Publishing new balloons
 * - Updating existing balloons
 * - Image uploads
 * 
 * Uses ts-core's BeatAPIBalloonEventsRepository internally.
 */

import {
  BeatAPIBalloonEventsRepository,
  BalloonEventModel,
  MetadataModel,
  DynamicEventBuilder,
  DescriptionType,
  ContentType,
  EventDateModel,
  ContactType as TsCoreContactType,
} from 'ts-core';
import type { ApiEndpoints, CategoryModel } from 'ts-core';
import type { EventFormData } from '../types';
import { dataUrlToFile, isDataUrl, getPendingFile } from './imageService';
import { reverseGeocodeLocation, toCountryCode } from './mapbox';
import { isVideoLinkUrl } from '../utils/imageUtils';

export interface BalloonServiceConfig {
  apiEndpoints: ApiEndpoints;
  apiKey: string;
  metadata: MetadataModel;
  partnerKey?: string;
}

export interface PublishResult {
  success: boolean;
  eventId?: string;
  slug?: string;
  lng?: number;
  lat?: number;
  error?: string;
  statusCode?: number;
  imageUploadErrors?: string[];
}

/**
 * Create a balloon events repository instance
 */
function createRepository(config: BalloonServiceConfig): BeatAPIBalloonEventsRepository {
  return new BeatAPIBalloonEventsRepository(
    config.metadata,
    config.apiEndpoints,
    config.apiKey,
    false, // logs
    [], // transformerPlugins
    [], // excludedProviders
    config.partnerKey || null
  );
}

/**
 * Fetch a balloon by slug or ID
 * The API accepts both slug and ID as the identifier
 */
export async function fetchBalloonByIdentifier(
  config: BalloonServiceConfig,
  identifier: string
): Promise<BalloonEventModel | null> {
  try {
    const repository = createRepository(config);
    const event = await repository.load(identifier, true); // invalidateCache = true for fresh data
    return event;
  } catch (error) {
    console.error('[BalloonService] Failed to fetch balloon:', error);
    return null;
  }
}

/**
 * Singleton service class for balloon operations
 */
export class BalloonService {
  private config: BalloonServiceConfig;
  private repository: BeatAPIBalloonEventsRepository;

  constructor(config: BalloonServiceConfig) {
    this.config = config;
    this.repository = createRepository(config);
  }

  /**
   * Fetch a balloon by slug or ID
   */
  async getBalloon(identifier: string, invalidateCache = true): Promise<BalloonEventModel | null> {
    try {
      const event = await this.repository.load(identifier, invalidateCache);
      return event;
    } catch (error) {
      console.error('[BalloonService] Failed to fetch balloon:', error);
      return null;
    }
  }

  /**
   * Check if a balloon exists in the local cache
   */
  hasCached(identifier: string): boolean {
    return this.repository.has(identifier);
  }

  /**
   * Get a balloon from local cache (does not fetch)
   */
  getCached(identifier: string): BalloonEventModel | null {
    return this.repository.get(identifier) || null;
  }
}

// Singleton instance management
let balloonServiceInstance: BalloonService | null = null;

export function getBalloonService(config: BalloonServiceConfig): BalloonService {
  if (
    !balloonServiceInstance ||
    balloonServiceInstance['config'].apiKey !== config.apiKey
  ) {
    balloonServiceInstance = new BalloonService(config);
  }
  return balloonServiceInstance;
}

/**
 * Transform EventFormData to BalloonEventModel for publishing
 */
export function transformFormDataToBalloonEvent(
  formData: EventFormData,
  metadata: MetadataModel,
  options: { includeExistingImages?: boolean } = {}
): BalloonEventModel {
  const { includeExistingImages = true } = options;
  const builder = new DynamicEventBuilder();

  // Title
  builder.title(formData.title);

  // Categories - resolve slugs to CategoryModel
  if (formData.categories && formData.categories.length > 0) {
    const allCategories = metadata.getAllCategories();
    const resolvedCategories = formData.categories
      .map(slug => {
        const found = allCategories.find(c => c.slug === slug || c.id === slug);
        return found;
      })
      .filter((c): c is CategoryModel => c !== undefined);
    if (resolvedCategories.length > 0) {
      builder.categories(resolvedCategories);
    }
  }

  // Descriptions - combine all description types
  const descriptions: Array<{
    type: DescriptionType;
    content: string;
    contentType: ContentType;
    languageIso: string;
  }> = [];

  // Main description - use HTML for rich text editor support
  if (formData.description) {
    descriptions.push({
      type: 'main' as DescriptionType,
      content: formData.description,
      contentType: ContentType.HTML,
      languageIso: 'en',
    });
  }

  // Subtitle - use HTML for rich text editor support
  if (formData.subtitle) {
    descriptions.push({
      type: 'subtitle' as DescriptionType,
      content: formData.subtitle,
      contentType: ContentType.HTML,
      languageIso: 'en',
    });
  }

  // Additional descriptions from DetailsStep - all use HTML
  if (formData.descriptions && formData.descriptions.length > 0) {
    for (const desc of formData.descriptions) {
      descriptions.push({
        type: desc.type as DescriptionType,
        content: desc.content,
        contentType: ContentType.HTML,
        languageIso: desc.languageIso || 'en',
      });
    }
  }

  if (descriptions.length > 0) {
    builder.descriptions(descriptions);
  }

  // Location and Dates - combined as locationsDates
  if (formData.location && formData.dates && formData.dates.length > 0) {
    const addressComponents = formData.location.addressComponents;
    const locationDates = [{
      location: {
        name: formData.location.name || '',
        coordinates: {
          lat: formData.location.coordinates.lat,
          lng: formData.location.coordinates.lng,
        },
        address: {
          street: addressComponents?.street || formData.location.address || '',
          zip: addressComponents?.zip || '',
          locality: addressComponents?.locality || '',
          country: toCountryCode(addressComponents?.country || ''),
          region: addressComponents?.region || '',
        },
        contactInfos: [],
      },
      notes: '',
      dates: formData.dates.map(d => new EventDateModel(d.start, d.end ?? undefined, '')),
    }];
    builder.locationsDates(locationDates);
  }

  // Tags
  if (formData.tags && formData.tags.length > 0) {
    builder.tags(formData.tags);
  }

  // Contacts
  if (formData.contactInfos && formData.contactInfos.length > 0) {
    builder.contacts(formData.contactInfos.map(c => ({
      type: c.type as TsCoreContactType,
      content: c.content,
    })));
  }

  // Organizer
  if (formData.organisers && formData.organisers.length > 0) {
    const org = formData.organisers[0];
    if (org.name) {
      builder.organizer({
        name: org.name,
        designation: '',
        salutation: org.salutation || '',
        contacts: org.contactInfos?.map(c => ({
          type: c.type as TsCoreContactType,
          content: c.content,
        })) || [],
        location: org.location?.map(loc => ({
          name: loc.name || '',
          coordinates: loc.coordinates || { lat: 0, lng: 0 },
          address: {
            street: loc.address?.street || '',
            zip: loc.address?.zip || '',
            locality: loc.address?.locality || '',
            country: loc.address?.country || '',
            region: loc.address?.region || '',
          },
        })) || [],
      });
    }
  }

  // Images: for updates, always set the images array (even if empty) so the PUT body
  // tells the server exactly which images to keep. Removed images won't be included.
  // YouTube links are also included here as plain URL entries.
  // For creates/clones, skip — uploadEventImages + a follow-up PUT handles images.
  if (includeExistingImages) {
    const existingImages = formData.images
      ?.filter(img => img.url && !isDataUrl(img.url) && !img.url.startsWith('blob:'))
      .map(img => ({
        url: img.url,
        text: img.caption || img.text || '',
        copyright: img.copyright || '',
      })) || [];
    const videoLinkImages = (formData.youtubeLinks ?? [])
      .filter(url => url && isVideoLinkUrl(url))
      .map(url => ({ url, text: '', copyright: '' }));
    builder.images([...existingImages, ...videoLinkImages]);
  }

  return builder.build() as BalloonEventModel;
}

/**
 * Fetch a remote image URL and return it as a File object for re-upload
 */
async function fetchUrlAsFile(url: string): Promise<File> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  const mimeType = blob.type || 'image/jpeg';
  const extMap: Record<string, string> = {
    'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
    'image/gif': 'gif', 'video/mp4': 'mp4',
  };
  const ext = extMap[mimeType] || 'jpg';
  return new File([blob], `image-${Date.now()}.${ext}`, { type: mimeType });
}

/**
 * Upload images to an event after creation
 */
export async function uploadEventImages(
  repository: BeatAPIBalloonEventsRepository,
  eventId: string,
  token: string,
  images: EventFormData['images']
): Promise<{ success: boolean; errors: string[] }> {
  const errors: string[] = [];

  if (!images || images.length === 0) {
    return { success: true, errors: [] };
  }

  // Include data URL images (new uploads), HTTP URL images (cloned/existing),
  // and blob URL images (videos stored via pending file store)
  const imagesToUpload = images.filter(img =>
    img.url && (isDataUrl(img.url) || img.url.startsWith('http') || img.url.startsWith('blob:'))
  );

  for (let i = 0; i < imagesToUpload.length; i++) {
    const img = imagesToUpload[i];
    try {
      let file: File;

      if (isDataUrl(img.url)) {
        // New upload: convert data URL to File
        file = dataUrlToFile(img.url);
      } else if (img.url.startsWith('blob:')) {
        // File kept as blob URL — retrieve from pending store
        const pendingFile = getPendingFile(img.url);
        if (!pendingFile) {
          errors.push(`File ${i + 1}: file no longer available (session expired)`);
          continue;
        }
        file = pendingFile;
      } else {
        // Existing image (clone/edit): fetch and re-upload
        file = await fetchUrlAsFile(img.url);
      }

      // Upload file - note: text/caption not yet supported by backend
      const response = await repository.uploadFile(eventId, token, {
        file,
        metadata: {
          copyright: img.copyright || '',
        },
      });

      if (response.status !== 200) {
        errors.push(`Failed to upload image ${i + 1}: ${response.data?.error || 'Unknown error'}`);
      }
    } catch (error) {
      errors.push(`Failed to upload image ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return {
    success: errors.length === 0,
    errors,
  };
}

/**
 * Publish an event to the Balloon Events platform (create or update)
 */
export async function publishEvent(
  config: BalloonServiceConfig,
  formData: EventFormData,
  token: string
): Promise<PublishResult> {
  try {
    // Create repository
    const repository = createRepository(config);

    // Check if this is an update (editId is set)
    const isUpdate = !!formData.editId;
    const editId = formData.editId;

    // Ensure location has full address components before publishing
    let enrichedFormData = formData;
    if (
      formData.location.coordinates.lat !== 0 &&
      formData.location.coordinates.lng !== 0 &&
      (!formData.location.addressComponents?.locality || !formData.location.addressComponents?.country)
    ) {
      try {
        const fullLocation = await reverseGeocodeLocation(formData.location.coordinates);
        enrichedFormData = {
          ...formData,
          location: {
            ...formData.location,
            addressComponents: fullLocation.addressComponents,
          },
        };
      } catch (error) {
        console.warn('Failed to enrich location with address components:', error);
        // Continue with original data if geocoding fails
      }
    }

    // Transform form data to BalloonEventModel
    // For creates/clones: don't embed http URL images in the model — uploadEventImages handles all uploads
    // For updates: include existing http URL images to preserve them on the server
    const event = transformFormDataToBalloonEvent(enrichedFormData, config.metadata, {
      includeExistingImages: isUpdate,
    });

    // Create or update event based on editId
    let response: { status: number; data?: unknown };
    if (isUpdate && editId) {
      console.log('[publishEvent] Updating existing event:', editId);
      response = await repository.put(editId, event, token);
    } else {
      console.log('[publishEvent] Creating new event');
      response = await repository.post(event, token);
    }

    // API may return 200 or 201 for successful creation/update
    if (response.status !== 200 && response.status !== 201) {
      // Extract error message from response
      let errorMessage = isUpdate ? 'Failed to update event' : 'Failed to publish event';
      const data = response.data as Record<string, unknown> | undefined;
      if (data?.message) {
        errorMessage = typeof data.message === 'string' 
          ? data.message 
          : JSON.stringify(data.message);
      } else if (data?.error) {
        errorMessage = typeof data.error === 'string'
          ? data.error
          : JSON.stringify(data.error);
      }
      return {
        success: false,
        error: errorMessage,
        statusCode: response.status,
      };
    }

    // For updates, use the editId; for creates, get from response
    const responseData = response.data as Record<string, unknown> | undefined;
    const innerData = responseData?.data as Record<string, unknown> | undefined;
    const eventId = isUpdate ? editId : innerData?.id as string | undefined;
    if (!eventId) {
      return {
        success: false,
        error: isUpdate ? 'Event updated but no ID available' : 'Event created but no ID returned',
      };
    }

    // Extract slug and coordinates from API response for downstream notifications
    const eventSlug = innerData?.slug as string | undefined;
    const locationData = innerData?.location as Record<string, unknown> | undefined;
    const coordsData = locationData?.coordinates as Record<string, unknown> | undefined;
    const eventLat = coordsData?.lat != null ? Number(coordsData.lat) : undefined;
    const eventLng = coordsData?.lng != null ? Number(coordsData.lng) : undefined;

    // Upload images:
    // For creates/clones: upload ALL images (http URLs fetched + blob/data URLs converted)
    // For updates: only upload NEW images (blob/data URLs) — existing http URLs are preserved via the model
    const imagesToUpload = isUpdate
      ? enrichedFormData.images?.filter(img => img.url && !img.url.startsWith('http'))
      : enrichedFormData.images;

    console.log('[publishEvent] Uploading images:', {
      total: enrichedFormData.images?.length || 0,
      uploading: imagesToUpload?.length || 0,
      isUpdate,
    });
    const imageResult = await uploadEventImages(repository, eventId, token, imagesToUpload);

    // For creates: if there are YouTube links, do a follow-up PUT to merge them with
    // the uploaded CDN images. We must fetch the balloon first because the file upload
    // endpoint adds images independently — overwriting with just YouTube links would
    // discard the uploaded files.
    const videoLinks = enrichedFormData.youtubeLinks?.filter(url => isVideoLinkUrl(url)) ?? [];
    if (!isUpdate && videoLinks.length > 0) {
      try {
        const fetchedEvent = await repository.load(eventId, true);
        const cdnImages = (fetchedEvent?._images ?? [])
          .filter(img => !isVideoLinkUrl(img.url))
          .map(img => ({ url: img.url, caption: img.text, text: img.text, copyright: img.copyright }));

        const formDataForYoutubePut: EventFormData = {
          ...enrichedFormData,
          editId: eventId,
          images: cdnImages,
        };
        const eventWithYoutube = transformFormDataToBalloonEvent(formDataForYoutubePut, config.metadata, {
          includeExistingImages: true,
        });
        await repository.put(eventId, eventWithYoutube, token);
        console.log('[publishEvent] Attached', videoLinks.length, 'video link(s) to new event');
      } catch (ytError) {
        console.warn('[publishEvent] Failed to attach video links:', ytError);
        imageResult.errors.push('Video links could not be saved — please re-add them in edit mode');
      }
    }

    return {
      success: true,
      eventId,
      slug: eventSlug,
      lng: eventLng,
      lat: eventLat,
      imageUploadErrors: imageResult.errors.length > 0 ? imageResult.errors : undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}
